import {
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query'
import type { Coupon, CouponAPIResponse, UTM } from 'typings'
import { useFetch } from './useFetch'

export type UseCouponsReturn = Coupon[]

export function countCouponCodes(coupons: Coupon[]): number {
  return coupons.reduce(
    (count, coupon) => count + (coupon.grouping?.amount ?? 1),
    0
  )
}

export function filterCoupon(utm: UTM) {
  return function (coupon: Coupon) {
    const { utmSource, utmCampaign } = utm

    const hasNoUTMSourceFilter = !utmSource.length

    const couponMatchesUTMSource = utmSource.includes(
      coupon.configuration.utmSource ?? ''
    )

    const hasNoUTMCampaignFilter = !utmCampaign.length

    const couponMatchesUTMCampaign = utmCampaign.includes(
      coupon.configuration.utmCampaign ?? ''
    )

    return (
      (hasNoUTMSourceFilter || couponMatchesUTMSource) &&
      (hasNoUTMCampaignFilter || couponMatchesUTMCampaign)
    )
  }
}

export function useCoupons(options: UseQueryOptions<UseCouponsReturn>) {
  const { fetcherWrapper } = useFetch()

  const queryClient = useQueryClient()

  const url = new URL(`/api/proxy/rnb/v2/pvt/coupon`, window.location.origin)

  async function fetcher() {
    return fetcherWrapper(url.toString())
      .then((r) => r.json())
      .then(({ items }: CouponAPIResponse) => {
        return items
      })
  }

  function countCouponsByUTM(utm: UTM): number | undefined {
    const coupons = queryClient.getQueryData<UseCouponsReturn | undefined>(
      options.queryKey
    )

    if (!coupons) return undefined

    const matchedCoupons = coupons.filter(filterCoupon(utm))

    return countCouponCodes(matchedCoupons)
  }

  return {
    ...useQuery<UseCouponsReturn>({ ...options, queryFn: fetcher }),
    countCouponsByUTM,
  }
}
